import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/http";
import { toast } from "sonner";
import { Check, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const [tx, setTx] = useState(null);
  const attempts = useRef(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!sid) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await http.get(`/payments/status/${sid}`);
        setTx(data);
        if (data.payment_status === "paid") { setState("paid"); toast.success("Payment confirmed."); return; }
        if (data.status === "expired") { setState("expired"); return; }
      } catch (e) { /* ignore */ }
      attempts.current += 1;
      if (attempts.current >= 10) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sid]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-4">
      <div className="relative w-full max-w-md card-surface gradient-border p-10 text-center">
        {state === "polling" && (
          <>
            <div className="ring-dot mx-auto mb-6" />
            <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">Confirming transmission…</div>
            <h1 className="mt-3 font-display font-black tracking-tighter text-3xl">One moment.</h1>
            <p className="mt-2 text-white/60 text-sm">Your photons are being verified.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="mx-auto mb-6 w-14 h-14 border border-[#00E5FF]/50 rounded-full flex items-center justify-center text-[#00E5FF]">
              <Check />
            </div>
            <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">Payment confirmed</div>
            <h1 className="mt-3 font-display font-black tracking-tighter text-3xl" data-testid="payment-success">Welcome, {tx?.tier?.toUpperCase().replace("_", " • ")}.</h1>
            <p className="mt-3 text-white/60 text-sm">Your subscription is active for 31 days. The door is always open.</p>
            <Link to="/hologram-club/my-reservations" className="btn-neon inline-block mt-8" data-testid="payment-success-continue">
              Go To Dashboard
            </Link>
          </>
        )}
        {(state === "failed" || state === "expired") && (
          <>
            <div className="mx-auto mb-6 w-14 h-14 border border-[#FF007F]/50 rounded-full flex items-center justify-center text-[#FF007F]">
              <XCircle />
            </div>
            <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#FF007F]">
              {state === "expired" ? "Session expired" : "Still pending"}
            </div>
            <h1 className="mt-3 font-display font-black tracking-tighter text-3xl">No charge confirmed.</h1>
            <p className="mt-3 text-white/60 text-sm">
              {state === "expired"
                ? "Your checkout session expired. Please try again."
                : "We'll keep checking — or head back to dashboard for status updates."}
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <button onClick={() => nav("/hologram-club/my-reservations")} className="btn-ghost" data-testid="payment-back-dashboard">
                Dashboard
              </button>
              <button onClick={() => nav("/hologram-club")} className="btn-neon" data-testid="payment-back-home">
                Back to Club
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
